"use client";

/**
 * Global listener for `upload:job-complete` events. Mounted at the app shell
 * so it stays alive even when the upload modal is closed. Shows a toast when
 * a job finishes in the background.
 *
 * Skips jobs already handled inline by the modal's success view (deduped via
 * lib/upload-jobs.ts), so users who stayed in the modal don't get a redundant
 * toast on top of the inline success state.
 */

import { useCallback } from "react";
import { useUploadJobComplete } from "@/shared/hooks/use-socket";
import { useToast } from "@/shared/hooks/use-toast";
import { isHandledByModal } from "@/shared/lib/upload-jobs";
import type { UploadJobCompletePayload } from "@/shared/lib/websocket";

export function UploadCompleteListener() {
  const { toast } = useToast();

  const handleComplete = useCallback(
    (payload: UploadJobCompletePayload) => {
      if (isHandledByModal(payload.jobId)) return;

      const itemWord = (n: number) => `${n} item${n === 1 ? "" : "s"}`;

      if (payload.status === "completed") {
        toast({
          title: "Upload successful",
          description: `${itemWord(payload.successfulItems)} added to your wardrobe!`,
        });
      } else if (payload.status === "partial") {
        toast({
          title: "Upload partially complete",
          description: `${itemWord(payload.successfulItems)} added, ${payload.failedItems} failed. ${payload.creditsRefunded} coins refunded.`,
        });
      } else {
        toast({
          title: "Upload failed",
          description: `All items failed to process. ${payload.creditsRefunded} coins refunded.`,
          variant: "destructive",
        });
      }
    },
    [toast]
  );

  useUploadJobComplete(handleComplete, [handleComplete]);

  return null;
}
